import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Copy, Save, RotateCcw, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PageHeader, EthicsNote } from "../components/common";
import { API, api, formatErr, CHANNELS, copy, shortUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const KINDS = {
  instagram: ["Legenda de feed", "Roteiro de Reels", "Sequência de Stories", "Carrossel"],
  facebook: ["Post de página", "Post para grupo"],
  tiktok: ["Roteiro de vídeo", "Legenda curta"],
  shorts: ["Roteiro de Shorts", "Título e descrição"],
  whatsapp: ["Mensagem de oferta", "Texto para status"],
};
const TONES = [["direto", "Direto / promocional"], ["curiosidade", "Curiosidade / storytelling"], ["luxo", "Exclusivo / sofisticado"], ["review", "Review sincero"], ["divertido", "Divertido"]];

async function streamGenerate(payload, onDelta, onDone) {
  const opts = { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) };
  let res = await fetch(`${API}/ai/generate`, opts);
  if (res.status === 401) { await api.post("/auth/refresh"); res = await fetch(`${API}/ai/generate`, opts); }
  if (!res.ok) throw { detail: (await res.json().catch(() => ({}))).detail || "Erro na geração" };
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const d = JSON.parse(p.slice(6));
      if (d.error) throw { detail: d.error };
      if (d.delta) onDelta(d.delta);
      if (d.done) onDone(d.credits);
    }
  }
}

const Sel = ({ label, value, onChange, children, testid }) => (
  <div className="space-y-1.5"><Label className="text-xs text-zinc-400">{label}</Label><select className="field" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>{children}</select></div>
);

export default function Generator() {
  const { user, setUser, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [links, setLinks] = useState([]);
  const [f, setF] = useState({ channel: "instagram", kind: KINDS.instagram[0], tone: "direto", product_id: params.get("product") || "", product_text: "", link: "", extra: "" });
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const outRef = useRef(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/links").then((r) => setLinks(r.data));
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "channel" ? { kind: KINDS[v][0] } : {}) }));

  const generate = async () => {
    if (!f.product_id && !f.product_text.trim()) return toast.error("Selecione um produto ou descreva o achadinho");
    setBusy(true); setOut("");
    try {
      await streamGenerate(f, (t) => setOut((o) => o + t), (c) => setUser((u) => ({ ...u, credits: c })));
      toast.success("Conteúdo gerado! -1 crédito");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); refreshUser(); }
  };

  const saveToLibrary = async () => {
    const prod = products.find((p) => p.id === f.product_id);
    try {
      await api.post("/contents", { title: `${prod?.title || f.product_text.slice(0, 40) || "Conteúdo"} • ${f.kind}`, channel: f.channel, kind: f.kind, body: out, product_id: f.product_id || "" });
      toast.success("Salvo na biblioteca!");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Inteligência artificial" title="Gerador de conteúdo" desc="Legendas, roteiros e mensagens prontas para cada rede — com transparência de publicidade." />
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="generator-channel-tabs">
        {CHANNELS.map((c) => (
          <button key={c.id} onClick={() => set("channel", c.id)} data-testid={`generator-channel-${c.id}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${f.channel === c.id ? "bg-amber-500/15 border-amber-500/50 text-amber-200" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
            <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: c.color }} />{c.label}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="card-lux p-5 space-y-4 lg:col-span-2">
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Formato" value={f.kind} onChange={(v) => set("kind", v)} testid="generator-kind-select">{KINDS[f.channel].map((k) => <option key={k}>{k}</option>)}</Sel>
            <Sel label="Tom de voz" value={f.tone} onChange={(v) => set("tone", v)} testid="generator-tone-select">{TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Sel>
          </div>
          <Sel label="Produto" value={f.product_id} onChange={(v) => set("product_id", v)} testid="generator-product-select">
            <option value="">— Descrever manualmente —</option>{products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </Sel>
          {!f.product_id && (
            <div className="space-y-1.5"><Label className="text-xs text-zinc-400">Descreva o achadinho</Label>
              <Textarea rows={3} className="bg-zinc-900 border-zinc-800" value={f.product_text} onChange={(e) => set("product_text", e.target.value)} placeholder="Ex.: Mini processador elétrico, 250ml, USB recarregável, R$ 39,90" data-testid="generator-product-text" /></div>
          )}
          <Sel label="Link rastreável (opcional)" value={f.link} onChange={(v) => set("link", v)} testid="generator-link-select">
            <option value="">Usar link do produto</option>{links.map((l) => <option key={l.id} value={shortUrl(l.code)}>{l.title}</option>)}
          </Sel>
          <div className="space-y-1.5"><Label className="text-xs text-zinc-400">Instruções extras</Label>
            <Textarea rows={2} className="bg-zinc-900 border-zinc-800" value={f.extra} onChange={(e) => set("extra", e.target.value)} placeholder="Ex.: público de mães, foco em praticidade" data-testid="generator-extra-input" /></div>
          <Button onClick={generate} disabled={busy} className="btn-gold w-full h-12 rounded-full text-base" data-testid="generator-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Gerar (1 crédito)
          </Button>
          <div className="text-xs text-zinc-500 flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-amber-400" />Saldo: <span className="text-amber-300 font-semibold" data-testid="generator-credits">{user.credits}</span> créditos</div>
        </div>
        <div className="card-lux p-5 lg:col-span-3 flex flex-col min-h-[380px]">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Resultado • {CHANNELS.find((c) => c.id === f.channel)?.label}</div>
            {busy && <span className="text-xs text-amber-400 animate-pulse">escrevendo...</span>}
          </div>
          <Textarea ref={outRef} value={out} onChange={(e) => setOut(e.target.value)} placeholder="Seu conteúdo aparecerá aqui. Você pode editar antes de salvar."
            className="flex-1 min-h-[280px] bg-zinc-950/60 border-zinc-800 font-mono text-[13px] leading-relaxed" data-testid="generator-output" />
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Button variant="outline" className="rounded-full border-zinc-700" disabled={!out || busy} onClick={() => copy(out).then(() => toast.success("Copiado!"))} data-testid="generator-copy-button"><Copy className="h-4 w-4 mr-1" />Copiar</Button>
            <Button variant="outline" className="rounded-full border-zinc-700" disabled={!out || busy} onClick={generate} data-testid="generator-regenerate-button"><RotateCcw className="h-4 w-4 mr-1" />Variação</Button>
            <Button className="btn-gold rounded-full" disabled={!out || busy} onClick={saveToLibrary} data-testid="generator-save-button"><Save className="h-4 w-4 mr-1" />Salvar</Button>
          </div>
        </div>
      </div>
      <div className="mt-5"><EthicsNote>A IA segue regras éticas: não inventa preços, avaliações ou escassez, inclui identificação de publicidade (#publi) e não incentiva spam ou engajamento artificial. Revise sempre antes de publicar.</EthicsNote></div>
    </div>
  );
}
